import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { fetchPublicConfig } from '@/hooks/usePublicConfig';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i += 1) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function pushSupported() {
  return (
    typeof window !== 'undefined'
    && window.isSecureContext
    && 'serviceWorker' in navigator
    && 'PushManager' in window
    && 'Notification' in window
  );
}

async function saveSubscription(token, subscription) {
  const response = await fetch(`${API_URL}/api/push/subscriptions`, {
    method: 'POST',
    credentials: 'include',
    headers: token ? { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` } : { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      subscription: subscription.toJSON(),
      user_agent: navigator.userAgent,
      device_label: navigator.platform || 'browser',
    }),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Unable to save push subscription');
  }
  return response.json();
}

export function useWebPushRegistration() {
  const { user, token } = useAuth();
  const configRef = useRef(null);
  const registrationRef = useRef(null);
  const [state, setState] = useState({
    ready: false,
    loading: false,
    supported: false,
    configured: false,
    permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
    subscribed: false,
    error: null,
  });

  const registerPush = useCallback(async ({ requestPermission = false } = {}) => {
    if (!user || !token) return { ok: false, reason: 'auth' };

    if (!pushSupported()) {
      setState(prev => ({
        ...prev,
        ready: true,
        loading: false,
        supported: false,
        configured: false,
        permission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
        subscribed: false,
      }));
      return { ok: false, reason: 'unsupported' };
    }

    setState(prev => ({ ...prev, loading: true, supported: true, error: null }));

    try {
      const config = configRef.current || await fetchPublicConfig(token);
      configRef.current = config;

      const configured = Boolean(config.web_push_enabled && config.web_push_public_key);
      if (!configured) {
        setState(prev => ({
          ...prev,
          ready: true,
          loading: false,
          supported: true,
          configured: false,
          permission: Notification.permission,
          subscribed: false,
        }));
        return { ok: false, reason: 'not_configured' };
      }

      const registration = registrationRef.current || await navigator.serviceWorker.register('/push-sw.js');
      registrationRef.current = registration;

      let permission = Notification.permission;
      if (permission === 'default' && requestPermission) {
        permission = await Notification.requestPermission();
      }

      if (permission !== 'granted') {
        setState(prev => ({
          ...prev,
          ready: true,
          loading: false,
          supported: true,
          configured: true,
          permission,
          subscribed: false,
        }));
        return { ok: false, reason: permission };
      }

      const existingSubscription = await registration.pushManager.getSubscription();
      const subscription = existingSubscription || await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.web_push_public_key),
      });

      await saveSubscription(token, subscription);
      setState(prev => ({
        ...prev,
        ready: true,
        loading: false,
        supported: true,
        configured: true,
        permission,
        subscribed: true,
        error: null,
      }));
      return { ok: true };
    } catch (error) {
      setState(prev => ({
        ...prev,
        ready: true,
        loading: false,
        error: error.message || 'Unable to enable browser notifications',
      }));
      console.info('Web Push registration unavailable:', error.message);
      return { ok: false, reason: 'error', error };
    }
  }, [user, token]);

  useEffect(() => {
    let canceled = false;
    if (!user || !token) {
      configRef.current = null;
      registrationRef.current = null;
      setState(prev => ({
        ...prev,
        ready: false,
        loading: false,
        subscribed: false,
        error: null,
      }));
      return undefined;
    }

    registerPush({ requestPermission: false }).then(() => {
      if (canceled) return;
    });

    return () => {
      canceled = true;
    };
  }, [user, token, registerPush]);

  const requestBrowserNotifications = useCallback(
    () => registerPush({ requestPermission: true }),
    [registerPush]
  );

  return {
    ...state,
    requestBrowserNotifications,
  };
}
